import React, { useState } from 'react';
import { useAudioDropzone } from '@/hooks/useAudioDropzone';
import { Button } from '@jenmusic/jen-ui-library';
import { FileMusic, Plus, X } from 'lucide-react';
import { cn } from '@/utils';

interface AudioUploaderProps {
  onChange?: (files: File[]) => void;
  maxFiles?: number;
  minDuration?: number;
  maxDuration?: number;
}

export const AudioUploader: React.FC<AudioUploaderProps> = ({ onChange, maxFiles = 5, minDuration, maxDuration }) => {
  const [durationError, setDurationError] = useState<string | null>(null);

  const checkAudioDuration = async (file: File): Promise<boolean> => {
    return new Promise((resolve) => {
      const audio = new Audio();
      audio.preload = 'metadata';

      audio.onloadedmetadata = () => {
        const duration = audio.duration;

        if (minDuration && duration < minDuration) {
          setDurationError(`duration must be greater than ${minDuration} seconds`);
          resolve(false);
        } else if (maxDuration && duration > maxDuration) {
          setDurationError(`duration must be less than ${maxDuration} seconds`);
          resolve(false);
        } else {
          setDurationError(null);
          resolve(true);
        }
      };

      audio.onerror = () => {
        setDurationError('failed to verify audio duration');
        resolve(false);
      };

      audio.src = URL.createObjectURL(file);
    });
  };

  const { files, error, getRootProps, getInputProps, removeFile, clearFiles } = useAudioDropzone({
    maxFiles,
    onChange,
    beforeAddFile: async (file) => {
      if (minDuration || maxDuration) {
        return await checkAudioDuration(file);
      }
      return true;
    },
  });

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const truncateFileName = (fileName: string, maxLength = 20) => {
    if (fileName.length <= maxLength) return fileName;

    const extension = fileName.split('.').pop() || '';
    const nameWithoutExt = fileName.substring(0, fileName.length - extension.length - 1);

    if (nameWithoutExt.length <= maxLength - 3 - extension.length) return fileName;

    return `${nameWithoutExt.substring(0, maxLength - 3 - extension.length)}...${extension ? `.${extension}` : ''}`;
  };

  return (
    <div className="space-y-3">
      {files.length === 0 ? (
        <div
          {...getRootProps()}
          className=" flex flex-col justify-center items-center w-full h-[184px]  rounded-[8px] p-[14px] text-center cursor-pointer border-jenmusic-border-background border bg-jenmusic-background-primary   transition-colors"
        >
          <input {...getInputProps()} />
          <FileMusic width={24} className="mx-auto text-jenmusic-interactive" />
          <p className="mt-2 text-jenmusic-content-tertiary">Drag and drop or upload a file.</p>
          <p className="text-sm text-jenmusic-content-tertiary text-[12px]">
            Upload a track with the voice to be sampled. Please do not include any music or background noise.
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {files.map((file, index) => (
            <div key={index} className="flex items-center justify-between p-3 bg-jenmusic-background-secondary rounded-lg">
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <FileMusic className="h-5 w-5 text-jenmusic-interactive flex-shrink-0" />
                <div className="flex flex-col min-w-0">
                  <span className="text-sm font-medium text-jenmusic-content-primary truncate" title={file.name}>
                    {truncateFileName(file.name)}
                  </span>
                  {/* <span className="text-xs text-jenmusic-content-tertiary">{formatFileSize(file.size)}</span> */}
                </div>
              </div>
              <button
                onClick={() => removeFile(file)}
                className="text-jenmusic-content-tertiary hover:text-jenmusic-content-primary ml-2 flex-shrink-0"
                title="Remove file"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ))}

          {files.length < maxFiles && (
            <button
              {...getRootProps()}
              className="w-full mt-2 flex items-center justify-center p-2 border border-dashed border-jenmusic-border rounded-lg text-jenmusic-content-tertiary hover:text-jenmusic-interactive hover:border-jenmusic-interactive transition-colors"
            >
              <input {...getInputProps()} />
              <Plus className="h-4 w-4 mr-1" />
              <span className="text-sm">Add another file</span>
            </button>
          )}
        </div>
      )}

      {error && <p className="text-sm text-red-500">{error}</p>}
      {durationError && <p className="text-sm text-red-500">{durationError}</p>}
    </div>
  );
};
